import React from 'react';
import { FileText, DollarSign, PackageMinus, Clock } from 'lucide-react';

/**
 * RecentActivityList Component
 * قائمة آخر الأنشطة في الصفحة الرئيسية
 *
 * Protocols Applied:
 * - P-COMPONENT-001: Clean Component Structure
 * - P-RTL-001: RTL Support
 * - P-DARK-MODE-001: Dark Mode Support
 * - P-UX-001: Loading States
 *
 * @param {Object} props
 * @param {Array} props.activities - قائمة الأنشطة
 * @param {boolean} props.loading - حالة التحميل
 */

const RecentActivityList = ({ activities = [], loading = false }) => {
  // Icon mapping
  const iconMap = {
    invoice: FileText,
    payment: DollarSign,
    return: PackageMinus
  };

  // Color mapping
  const colorMap = {
    invoice: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    payment: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    return: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'الآن';
    if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
    if (diffHours < 24) return `منذ ${diffHours} ساعة`;
    if (diffDays < 7) return `منذ ${diffDays} يوم`;

    return date.toLocaleDateString('ar-KW', {
      day: 'numeric',
      month: 'short'
    });
  };

  // Format amount
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('ar-KW', {
      style: 'currency',
      currency: 'KWD',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(amount || 0);
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          آخر الأنشطة
        </h2>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="animate-pulse flex items-center gap-3 p-3">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (!activities || activities.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          آخر الأنشطة
        </h2>
        <div className="text-center py-12">
          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">
            لا توجد أنشطة حديثة
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
        آخر الأنشطة
      </h2>

      <div className="space-y-2">
        {activities.map((activity) => {
          const Icon = iconMap[activity.type] || FileText;
          const colorClass = colorMap[activity.type] || colorMap.invoice;

          return (
            <div
              key={`${activity.type}-${activity.id}`}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              {/* Icon */}
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                <Icon className="w-5 h-5" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {activity.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {activity.customer}
                </p>
              </div>

              {/* Amount & Date */}
              <div className="text-left flex-shrink-0">
                <p className="text-sm font-bold text-gray-900 dark:text-white">
                  {formatAmount(activity.amount)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(activity.createdAt || activity.date)}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* View All Link */}
      {activities.length >= 10 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium">
            عرض جميع الأنشطة ←
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentActivityList;
